import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import Modal from "../LightThemeModal";
import { styles } from "../../../src/styles/request_details";
import { AuthUser } from "state/auth_user";

interface SystemAdmin {
    id: number;
    name: string;
}

interface AssignmentCardProps {
    selectedAdmin: string;
    systemAdmins: SystemAdmin[];
    onAdminChange: (value: string) => void;
    isUpdating: boolean;
    isDisabled: boolean;
    user?: AuthUser;
}

interface AdminOption {
    value: string;
    label: string;
}
const radioStyles = {
    radioContainer: {
        display: "flex",
        gap: "24px",
        alignItems: "center",
    },
    radioOption: {
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        gap: "8px",
    },
    hiddenRadio: {
        position: "absolute" as const,
        opacity: 0,
        width: 0,
        height: 0,
        pointerEvents: "none" as const,
    },
    radioOuterCircle: {
        width: "20px",
        height: "20px",
        borderRadius: "50%",
        border: "2px solid #D1D5DB",
        backgroundColor: "#FFFFFF",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        transition: "all 0.2s ease",
        boxSizing: "border-box" as const,
    },
    radioOuterCircleChecked: {
        width: "20px",
        height: "20px",
        borderRadius: "50%",
        border: "2px solid #E5B42F",
        backgroundColor: "#FFFFFF",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        transition: "all 0.2s ease",
        boxSizing: "border-box" as const,
    },
    radioInnerDot: {
        width: "8px",
        height: "8px",
        borderRadius: "50%",
        backgroundColor: "#E5B42F",
        flexShrink: 0,
    },
    radioLabel: {
        fontSize: "12px",
        fontWeight: 500,
        color: "#333",
        userSelect: "none" as const,
    },
    // Summary row shown after selection
    selectedSummary: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "6px 12px",
        borderRadius: 15,
        border: "1.5px solid #E5B42F",
        backgroundColor: "#FFF9E6",
        fontSize: 12,
        fontWeight: 500,
        color: "#555",
    },
    selectedSummaryLeft: {
        display: "flex",
        alignItems: "center",
        gap: 6,
    },
    selectedSummaryLabel: {
        fontSize: 12,
        fontWeight: 500,
        color: "#333",
    },
    changeButton: {
        background: "none",
        border: "none",
        cursor: "pointer",
        color: "#999",
        fontSize: 12,
        padding: "0 0 0 8px",
        lineHeight: 1,
        display: "flex",
        alignItems: "center",
    },
}
type AssignMode = "self" | "other" | null;

const AssignmentCard: React.FC<AssignmentCardProps> = ({
    selectedAdmin,
    systemAdmins,
    onAdminChange,
    isUpdating,
    isDisabled,
    user
}) => {
    const [assignMode, setAssignMode] = useState<AssignMode>(
        selectedAdmin === "self" ? "self" : selectedAdmin !== "all" ? "other" : null
    );
    const [openAdminDropdown, setOpenAdminDropdown] = useState(false);
    const [adminSearchQuery, setAdminSearchQuery] = useState("");
    const [isDropdownHovered, setIsDropdownHovered] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingAdminSelection, setPendingAdminSelection] = useState<{
        value: string;
        label: string;
        mode: AssignMode;
    } | null>(null);

    const adminDropdownRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const currentUserId = user?.id;

    // Determines if an admin is fully selected
    const isAdminSelected =
        selectedAdmin !== "all" && selectedAdmin !== "" && selectedAdmin != null;

    useEffect(() => {
        setOpenAdminDropdown(false);
        setAdminSearchQuery("");

        if (selectedAdmin === "self" || selectedAdmin === user?.id?.toString()) {
            setAssignMode("self");
        } else if (selectedAdmin !== "all" && selectedAdmin !== "") {
            setAssignMode("other");
        } else {
            setAssignMode(null);
        }
    }, [selectedAdmin, user?.id]);

    const adminOptions = useMemo<AdminOption[]>(() => [
        { value: "all", label: "Select Assignee" },
        ...[...systemAdmins]
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
            .map((admin) => ({
                value: admin.id.toString(),
                label: admin.name,
            })),
    ], [systemAdmins]);

    const filteredOptions = useMemo(() => {
        const filteredOpts = adminOptions.filter((opt) => opt.value !== "all");
        if (!adminSearchQuery.trim()) return filteredOpts;
        const query = adminSearchQuery.toLowerCase();
        return filteredOpts.filter((option) =>
            option.label.toLowerCase().includes(query)
        );
    }, [adminOptions, adminSearchQuery]);
    // Memoize selected option
    const selectedOption = useMemo(() =>
        selectedAdmin !== "all" && selectedAdmin !== "self"
            ? adminOptions.find((opt) => opt.value === selectedAdmin)
            : null,
        [selectedAdmin, adminOptions]
    );

    const isDropdownDisabled = isUpdating || isDisabled;
    // Memoize selected option
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                adminDropdownRef.current &&
                !adminDropdownRef.current.contains(event.target as Node)
            ) {
                setOpenAdminDropdown(false);
                setAdminSearchQuery("");
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleRadioChange = useCallback((mode: AssignMode) => {
        if (isDropdownDisabled) return;

        if (mode === "self") {
            setPendingAdminSelection({
                value: currentUserId?.toString() ?? "self",
                label: "Self",
                mode: "self",
            });
            setConfirmOpen(true);
            return;
        }

        setAssignMode(mode);
        setOpenAdminDropdown(false);
        setAdminSearchQuery("");
    }, [isDropdownDisabled, currentUserId]);

    const handleToggleDropdown = useCallback(() => {
        if (isDropdownDisabled) return;
        setOpenAdminDropdown((prev) => !prev);
    }, [isDropdownDisabled]);

    const handleAdminSelect = useCallback((value: string) => {
        if (isDropdownDisabled) return;

        const option = adminOptions.find((opt) => opt.value === value);
        setPendingAdminSelection({
            value,
            label: option?.label ?? "Selected admin",
            mode: "other",
        });
        setConfirmOpen(true);
    }, [adminOptions, isDropdownDisabled]);

    const handleConfirmSelection = useCallback(() => {
        if (!pendingAdminSelection) return;

        onAdminChange(pendingAdminSelection.value);
        setConfirmOpen(false);
        setPendingAdminSelection(null);
        setOpenAdminDropdown(false);
        setAdminSearchQuery("");
        setAssignMode(pendingAdminSelection.mode);
    }, [onAdminChange, pendingAdminSelection]);

    const handleCancelSelection = useCallback(() => {
        setConfirmOpen(false);
        setPendingAdminSelection(null);
    }, []);

    const handleSearchChange = useCallback((value: string) => {
        setAdminSearchQuery(value);
    }, []);


    const renderDropdownTrigger = () => {
        const displayLabel = selectedOption?.label || "Select Assignee";

        return (
            <div
                style={{
                    borderRadius: 15,
                    fontSize: 12,
                    height: 32,
                    border: `1.5px solid ${isDropdownHovered && !isUpdating && !isDisabled ? "#888" : "#D1D5DB"}`,
                    backgroundColor: isUpdating || isDisabled ? "#f9fafb" : "#fff",
                    cursor: isUpdating || isDisabled ? "not-allowed" : "pointer",
                    padding: "0 35px 0 12px",
                    display: "flex",
                    alignItems: "center",
                    transition: "all 0.2s",
                    whiteSpace: "nowrap" as const,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    opacity: isUpdating || isDisabled ? 0.6 : 1,
                    position: "relative" as const,
                }}
                onClick={handleToggleDropdown}
                onMouseEnter={() => setIsDropdownHovered(true)}
                onMouseLeave={() => setIsDropdownHovered(false)}
            >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {isUpdating ? "Updating..." : displayLabel}
                </span>
                {isUpdating ? (
                    <span style={{
                        position: "absolute", right: 12, top: "50%",
                        transform: "translateY(-50%)", color: "#888",
                        fontSize: 14, animation: "spin 1s linear infinite",
                    }}>⟳</span>
                ) : (
                    <span style={{
                        position: "absolute", right: 12, top: "50%",
                        transform: `translateY(-50%) rotate(${openAdminDropdown ? "270deg" : "90deg"})`,
                        color: "#888", fontSize: 18, pointerEvents: "none",
                        transition: "transform 0.2s ease", fontWeight: 600,
                    }}>›</span>
                )}
            </div>
        );
    };

    const renderDropdownMenu = () => {
        if (!openAdminDropdown || isDropdownDisabled) return null;

        return (
            <div style={{
                position: "absolute", top: 42, left: 0, right: 0,
                border: "1px solid #ccc", borderRadius: 6, backgroundColor: "#fff",
                zIndex: 10, boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                maxHeight: 180, overflowY: "auto",
            }}>
                <SearchInput
                    ref={searchInputRef}
                    value={adminSearchQuery}
                    onChange={handleSearchChange}
                />
                {selectedAdmin !== "all" && selectedAdmin !== "self" && (
                    <ClearSelectionOption onClick={() => {
                        onAdminChange("all");
                        setOpenAdminDropdown(false);
                        setAdminSearchQuery("");
                    }} />
                )}
                {filteredOptions.length > 0 ? (
                    <AdminOptionsList
                        options={filteredOptions}
                        selectedAdmin={selectedAdmin}
                        onSelect={handleAdminSelect}
                    />
                ) : (
                    <div style={styles.noResults}>No admins found</div>
                )}
            </div>
        );
    };

    // Custom radio button renderer
    const renderRadioOption = (
        mode: "self" | "other",
        label: string,
        id: string
    ) => {
        const isChecked = assignMode === mode;
        return (
            <div
                style={radioStyles.radioOption}
                onClick={() => handleRadioChange(mode)}
            >
                <input
                    type="radio"
                    id={id}
                    name="assignMode"
                    value={mode}
                    checked={isChecked}
                    readOnly
                    disabled={isDropdownDisabled}
                    style={radioStyles.hiddenRadio}
                />
                <div
                    style={
                        isChecked
                            ? radioStyles.radioOuterCircleChecked
                            : radioStyles.radioOuterCircle
                    }
                >
                    {isChecked && <div style={radioStyles.radioInnerDot} />}
                </div>
                <label htmlFor={id} style={{
                    ...radioStyles.radioLabel,
                    cursor: isDropdownDisabled ? "not-allowed" : "pointer",
                    opacity: isDropdownDisabled ? 0.6 : 1,
                }}>
                    {label}
                </label>
            </div>
        );
    };

    // Render summary — on radio buttons selction
    const renderSelectedSummary = () => {
        const isSelf = selectedAdmin === user?.id?.toString();
        const label = isSelf
            ? "Assigned to Self"
            : selectedOption?.label || "Assigned to Other";

        return (
            <div style={radioStyles.selectedSummary}>
                <div style={radioStyles.selectedSummaryLeft}>
                    <span style={radioStyles.selectedSummaryLabel}>{label}</span>
                </div>
            </div>
        );
    };

    return (
        <div style={styles.assignmentCard}>
            <h3 style={styles.assignmentHeading}>
                {isDisabled ? "Assigned Admin" : "Request is open"}
            </h3>
            {!isDisabled && (
                <p style={styles.assignmentText}>
                    Please select a system admin to assign this request.
                </p>
            )}

            <div style={{ maxWidth: "250px" }}>
                {/* Show summary pill when admin is selected, otherwise show radio buttons */}
                {isAdminSelected ? (
                    renderSelectedSummary()
                ) : (
                    <div style={radioStyles.radioContainer}>
                        {renderRadioOption("self", "Assign to Self", "assign-self")}
                        {renderRadioOption("other", "Assign Other", "assign-other")}
                    </div>
                )}

                {/* Show "Assigning..." text when self is selected and loader till api response */}
                {assignMode === "self" && !isAdminSelected && isUpdating && (
                    <div style={{ marginTop: 10, fontSize: 12, color: "#E5B611", display: "inline-flex", alignItems: "center", gap: 6 }}>
                        <span style={{
                            display: "inline-block",
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            border: "2px solid #F5DFA0",
                            borderTopColor: "#E5B611",
                            animation: "spin 0.8s linear infinite",
                            flexShrink: 0,
                            marginTop: 6,
                        }} />
                        Assigning...
                    </div>
                )}

                {/* Show dropdown only when mode is "other" and no admin selected */}
                {assignMode === "other" && !isAdminSelected && (
                    <div style={{ ...styles.filterGroup, marginTop: 10 }}>
                        <div style={{ position: "relative" }} ref={adminDropdownRef}>
                            {renderDropdownTrigger()}
                            {renderDropdownMenu()}
                        </div>
                    </div>
                )}
            </div>

            <Modal
                show={confirmOpen}
                onClose={handleCancelSelection}
                title="Confirm Admin Assignment"
                subText={
                    pendingAdminSelection
                        ? `Are you sure you want to assign this request to ${pendingAdminSelection.label}?`
                        : "Are you sure you want to assign this request?"
                }
                width="420px"
                closeOnBackdropClick={false}
            >
                <div style={styles.modalButtonGroup}>
                    <button
                        className="primary-button"
                        style={{ minWidth: "120px" }}
                        onClick={handleCancelSelection}
                    >
                        Cancel
                    </button>
                    <button
                        className="primary-button"
                        style={{ minWidth: "120px" }}
                        onClick={handleConfirmSelection}
                    >
                        OK
                    </button>
                </div>
            </Modal>

            <style>{`
                @keyframes spin {
                    from { transform: translateY(-50%) rotate(0deg); }
                    to { transform: translateY(-50%) rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

interface SearchInputProps {
    value: string;
    onChange: (value: string) => void;
}

const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
    ({ value, onChange }, ref) => (
        <div
            style={{
                padding: "8px",
                borderBottom: "1px solid #E3E6EA",
            }}
        >
            <input
                ref={ref}
                type="text"
                placeholder="Search admin..."
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                style={styles.searchInput}
                autoFocus
            />
        </div>
    )
);

SearchInput.displayName = "SearchInput";

interface ClearSelectionOptionProps {
    onClick: () => void;
}

const ClearSelectionOption: React.FC<ClearSelectionOptionProps> = ({ onClick }) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <div
            onClick={(e) => {
                e.stopPropagation();
                onClick();
            }}
            style={{
                fontSize: 12,
                padding: "8px 16px",
                cursor: "pointer",
                backgroundColor: isHovered ? "#f5f5f5" : "#fff",
                color: "#999",
                borderBottom: "1px solid #E3E6EA",
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            Clear selection
        </div>
    );
};

interface AdminOptionsListProps {
    options: AdminOption[];
    selectedAdmin: string;
    onSelect: (value: string) => void;
}

const AdminOptionsList: React.FC<AdminOptionsListProps> = ({
    options,
    selectedAdmin,
    onSelect,
}) => (
    <>
        {options.map((option) => (
            <AdminOptionItem
                key={option.value}
                option={option}
                isSelected={selectedAdmin === option.value}
                onSelect={onSelect}
            />
        ))}
    </>
);

interface AdminOptionItemProps {
    option: AdminOption;
    isSelected: boolean;
    onSelect: (value: string) => void;
}

const AdminOptionItem: React.FC<AdminOptionItemProps> = ({
    option,
    isSelected,
    onSelect,
}) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <div
            onClick={(e) => {
                e.stopPropagation();
                onSelect(option.value);
            }}
            style={{
                fontSize: 12,
                padding: "8px 16px",
                cursor: "pointer",
                backgroundColor: isSelected || isHovered ? "#f5f5f5" : "#fff",
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {option.label}
        </div>
    );
};

export default AssignmentCard;